import React, { useContext, useState } from 'react';
import { appChangeWarningBody } from '@components/CrossAppChangeDialog';
import { IconVariant, ItemWithLogo } from '@components/ItemWithLogo';
import { AvConfirmDialog, AvDialog, CrossAppHeader, RadioButton , WorkflowType } from '@fabric-ui/components';
import { Delete } from '@fabric-ui/icons';
import { flex } from '@fabric-ui/theme';
import { Alert, Box, Button } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { useAvContext } from '../../context/AvContextProvider';
import { NotificationContext } from '../../context/AvSnackBarProvider';
import { FeatureFlags } from '../../types';
import { useCancelWorkflow } from './hooks';

export enum SupportedSchedulesType {
  NONE = 'NONE',
  All = 'ALL',
  FULL_ONLY = 'FULL_ONLY',
}

export enum SyncMode {
  FULL_REFRESH = 'FULL_REFRESH',
  INCREMENTAL = 'INCREMENTAL',
}

export const syncModeMap = {
  incremental: 'Incremental',
  full_refresh: 'Full',
};

export const getSourceAuditTitle = dsiData => (
  <>
    <ItemWithLogo type={dsiData?.mappedSourceName} variant={IconVariant.sourcesMapByName} logoWidth={40} hideText />
    Audit Logs {dsiData?.name}
  </>
);

const options = [
  { title: 'Keep the data source, delete the underlying data', value: 'true' },
  { title: 'Delete all', value: 'false' },
];
interface DeleteDialogProps {
  dsis: any[];
  onClose(isApplied): unknown;
}
export const DeleteDSIsDialog: React.FC<DeleteDialogProps> = ({ dsis, onClose }) => {
  const { api, hasMultipleAppsInPayingAccount, featureFlags } = useAvContext();
  const queryClient = useQueryClient();
  const { runInfo, setRunInfo } = useContext(NotificationContext);
  const [keepDsi, setKeepDsi] = useState(options[0].value);
  const { mutate } = useMutation({
    mutationFn: () =>
      api(DELETE, { options: { ids: dsis.map(({ id }) => id), keepDsi: keepDsi === 'true' } }).then(({ data }) => {
        const runInfoRes = Object.values(data)[0];
        (Array.isArray(runInfoRes) ? runInfoRes : [runInfoRes]).forEach(({ workflowRun: { id, runId } }) =>
          setRunInfo({
            wfId: id,
            runId,
            returnStatusOnly: true,
            workflowType: WorkflowType.DataDeletion,
            onSuccess: () => runInfo.length === 0 && queryClient.refetchQueries({ queryKey: ['sourcesManagement'] }),
          })
        );
      }),
  });
  const apply = () => {
    mutate();
    onClose(true);
  };

  return (
    <AvDialog
      open={!!dsis}
      onClose={() => onClose(false)}
      titleVariant="error"
      noPadding
      title={`Delete data from source${dsis.length > 1 ? 's' : ''}`}
      footer={
        <Box sx={{ ...flex.justifyEndCenter, gap: 2 }}>
          <Button onClick={() => onClose(false)}>Cancel</Button>
          <Button onClick={apply} variant="contained" color="error">
            <Delete />
            Delete
          </Button>
        </Box>
      }
      header={hasMultipleAppsInPayingAccount && featureFlags[FeatureFlags.CrossAppChangePopUp] && <CrossAppHeader />}>
      <Box sx={{ px: 3, ...flex.col, gap: 2, maxWidth: 620 }}>
        <div>
          You are about to delete data from <span style={{ fontWeight: 600 }}>{dsis.map(({ name }) => name).join(', ')}</span>
          {hasMultipleAppsInPayingAccount && featureFlags[FeatureFlags.CrossAppChangePopUp] && appChangeWarningBody(true)}
          <Box sx={{ ...flex.col, mt: 3 }}>
            <span>Please choose one of the following options:</span>
            <RadioButton value={keepDsi} onChange={setKeepDsi} options={options} isHorizontal={false} />
          </Box>
        </div>
        <Alert severity="error" icon={false}>
          All data will be permanently deleted from the system and cannot be restored
        </Alert>
        Are you sure you want to delete?
      </Box>
    </AvDialog>
  );
};

const DELETE = gql`
  mutation deleteDsiData($ids: [String!]!, $keepDsi: Boolean) {
    deleteDsiData(dsiIds: $ids, keepDsi: $keepDsi) {
      workflowRun {
        id
        runId
      }
    }
  }
`;

interface CancelDsiRunDialogProps {
  dsi: any;
  onClose: (confirmed: boolean) => void;
  onSuccess: () => void;
}
export const CancelDsiRunDialog: React.FC<CancelDsiRunDialogProps> = ({ dsi, onClose, onSuccess }) => {
  const { mutateAsync: cancelWorkflow } = useCancelWorkflow(dsi.guidRuns);
  const apply = async () => {
    await cancelWorkflow({ onSuccess });
    onClose(true);
  };

  return (
    <AvDialog
      open={!!dsi}
      onClose={() => onClose(false)}
      titleVariant="warning"
      noPadding
      title="Cancel Workflow Run"
      footer={
        <Box sx={{ ...flex.row, justifyContent: 'flex-end', gap: 2 }}>
          <Button onClick={() => onClose(false)}>Keep running</Button>
          <Button onClick={apply} variant="contained">
            Yes, cancel
          </Button>
        </Box>
      }>
      <Box sx={{ px: 3, ...flex.col, gap: 2, maxWidth: 620 }}>
        <span>This process has been running for some time.</span>
        <span>Are you sure you want to cancel it?</span>
      </Box>
    </AvDialog>
  );
};

export const isFirstPartySourceType = (type: string): boolean => ['VIEW_PROJECTION', 'EXTERNAL_PROJECTION'].includes(type);
export const isFirstPartyDeliveryMethod = (type: string): boolean => ['AVALOR_QUERY_SERVICE_ICEBERG'].includes(type);
export const requireConsent = (type: string, deliveryMethod: string): boolean =>
  isFirstPartySourceType(type) || isFirstPartyDeliveryMethod(deliveryMethod);

export const FirstPartySourceConfirmDialog: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  sourceName: string;
}> = ({ isOpen, onClose, onConfirm, sourceName }) => (
  <AvConfirmDialog
    body={getConsentDialogBodyText(sourceName)}
    title="Grant Access For Web Traffic Logs"
    onClose={onClose}
    isOpen={isOpen}
    confirmButton={{
      text: 'Confirm',
      onClick: onConfirm,
    }}
    closeButton={{
      text: 'Cancel',
      onClick: onClose,
    }}
  />
);

const getConsentDialogBodyText = (dataStreamName: string) => (
  <>
    By enabling this data source, you consent to the <strong>{dataStreamName}</strong> data made available for necessary consumption.
    <br />
    Please be aware that authorized users will be able to view and interact with the data.
  </>
);

export const GET_SOURCES_FILTER_VALUES = gql`
  query findDataSourceInstanceFieldValuesByAccountId($fieldName: String, $filterablePage: FilterablePage) {
    findDataSourceInstanceFieldValuesByAccountId(fieldName: $fieldName, filterablePage: $filterablePage) {
      content
      totalPages
      totalElements
      number
      last
    }
  }
`;
